import { invoke } from '@tauri-apps/api/core';
import { isSyntheticRun } from '../utils/syntheticRun';
import { getVersion } from '@tauri-apps/api/app';
import packageJson from '../../package.json';
import { isTauri, sendHttpRequest } from '../utils/tauri';
import { loadAppPreferences, saveAppPreferences } from './appPreferencesStore';
import {
  getUpdatesBaseUrl,
  getUpdatesFallbackUrl,
  getUpdatesArtifactBaseUrl,
  buildLatestManifestUrl,
  buildManifestUrl,
  utcDayStamp,
  utcHalfDayStamp,
  isRepeatCheckToday,
  resolvePlatformKey,
  resolveArtifactUrl,
  resolveArtifactVersion,
  isUpdateAvailable,
  classifyUpdateError,
} from '../utils/updateUtils';

export {
  getUpdatesBaseUrl,
  getUpdatesFallbackUrl,
  getUpdatesArtifactBaseUrl,
  utcDayStamp,
  resolvePlatformKey,
  resolveArtifactUrl,
  resolveArtifactVersion,
  isUpdateAvailable,
  isManifestSelfConsistent,
  classifyUpdateError,
} from '../utils/updateUtils';

const CHECK_TIMEOUT_MS = 8000;
export const MANIFEST_CACHE_KEY = 'apiboo.updates.manifest';
export const MANIFEST_CACHED_ON_KEY = 'apiboo.updates.cachedOn';
export const MANIFEST_CACHED_VERSION_KEY = 'apiboo.updates.cachedVersion';
/**
 * The UTC day (`utcDayStamp`) on which a counted check last got an ANSWER from
 * `/latest`. This, not the half-day ledger, decides `repeat=1`: a counted
 * check that fell back to the CDN spends the half-day (ledger) but the server
 * never saw it, so the next check that reaches `/latest` that day must be
 * counted as the day's first.
 */
export const LATEST_REACHED_DAY_KEY = 'apiboo.updates.latestDay';
/**
 * How often a running app re-checks. Each tick inside the same UTC half-day
 * (00–12, 12–24) is a pure cache hit; only the first tick of a new half-day
 * makes the counted request (owner, 16.10.2026: twice a day, not once). So a
 * long-running app checks in within 6 h of 00:00 and of 12:00 UTC, and never
 * more than twice a day.
 */
export const RECHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

async function fetchWithBrowserTimeout(url, timeoutMs = CHECK_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      cache: 'no-store',
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

function parseManifestJson(rawBody) {
  try {
    return JSON.parse(rawBody);
  } catch {
    throw new Error('Invalid update manifest JSON');
  }
}

/**
 * Fetch JSON over HTTP. In Tauri uses Rust reqwest via send_http_request (no CORS).
 * @param {string} url
 * @param {number} [timeoutMs]
 * @returns {Promise<object>}
 */
async function fetchJson(url, timeoutMs = CHECK_TIMEOUT_MS) {
  const timeoutError = new Error('Update check timed out');

  const requestPromise = isTauri()
    ? sendHttpRequest({
        method: 'GET',
        url,
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
          'User-Agent': `Apiboo/${packageJson.version || '0.0.0'} (Tauri)`,
        },
      }).then((response) => {
        if (response.status < 200 || response.status >= 300) {
          throw new Error(`Manifest fetch failed: HTTP ${response.status}`);
        }
        return parseManifestJson(response.data);
      })
    : fetchWithBrowserTimeout(url, timeoutMs).then(async (response) => {
        if (!response.ok) {
          throw new Error(`Manifest fetch failed: HTTP ${response.status}`);
        }
        return response.json();
      });

  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(timeoutError), timeoutMs);
  });

  try {
    return await Promise.race([requestPromise, timeoutPromise]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Cached manifest for the current UTC half-day, or null on a miss. Corrupt or partial
 * cache entries are a miss — reading the cache must never throw.
 * @returns {object | null}
 */
export function readCachedManifest(version = '') {
  try {
    if (typeof localStorage === 'undefined') return null;
    if (localStorage.getItem(MANIFEST_CACHED_ON_KEY) !== utcHalfDayStamp()) return null;
    // A NEW version has not checked in yet today (owner, 04.10.2026): right
    // after an in-app update the counted check runs once more, so the server
    // (and the admin's install list) learns the running version at once —
    // not at the next UTC day. One extra counted request per device on its
    // update day; unique installs / active devices are counted server-side,
    // once a day, and do not move.
    if (version && localStorage.getItem(MANIFEST_CACHED_VERSION_KEY) !== version) return null;
    const raw = localStorage.getItem(MANIFEST_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Stamp the manifest with the current UTC half-day so the next check within the
 * same half-day makes no request at all — at most two counted requests per
 * device per day. The second one of a UTC day says `repeat=1`, so the server's
 * one-per-device-per-day active counter still matches its UTC bucketing.
 *
 * ⚠ `MANIFEST_CACHED_ON_KEY` IS THE COUNTED-CALL LEDGER, NOT A FRESHNESS
 * MARKER. A fetch that did not go through the counted `/updates/latest`
 * endpoint must pass `stampDay: false`: stamping it would consume the device's
 * one counted call for the half-day without ever making it, and the device would
 * silently vanish from the active-device metric. The cached BODY may still be
 * refreshed — only the ledger is off limits.
 *
 * @param {object} manifest
 * @param {{ stampDay?: boolean, version?: string }} [options]
 */
function writeCachedManifest(manifest, { stampDay = true, version = '' } = {}) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(MANIFEST_CACHE_KEY, JSON.stringify(manifest));
    if (stampDay) {
      localStorage.setItem(MANIFEST_CACHED_ON_KEY, utcHalfDayStamp());
      // Which version spent today's counted check (readCachedManifest).
      if (version) localStorage.setItem(MANIFEST_CACHED_VERSION_KEY, version);
    }
  } catch {
    // A full/blocked storage must not break the update check.
  }
}

/**
 * True when a counted check already reached `/latest` on today's UTC day —
 * this one is the afternoon check, or the extra one right after an in-app
 * update. A morning check that fell back to the CDN does not count.
 * @returns {boolean}
 */
function isRepeatCheck() {
  try {
    if (typeof localStorage === 'undefined') return false;
    const latestDay = localStorage.getItem(LATEST_REACHED_DAY_KEY);
    if (latestDay !== null) return latestDay === utcDayStamp();
    // A pre-16.10 build kept only a bare-day ledger (no half-day suffix, no
    // `latestDay`): treat it as today's first check having been made.
    const ledger = localStorage.getItem(MANIFEST_CACHED_ON_KEY);
    return typeof ledger === 'string' && ledger.length === 10 && isRepeatCheckToday(ledger);
  } catch {
    return false;
  }
}

/** Record that a counted check reached `/latest` today (see LATEST_REACHED_DAY_KEY). */
function markLatestReached() {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LATEST_REACHED_DAY_KEY, utcDayStamp());
  } catch {
    // A full/blocked storage must not break the update check.
  }
}

/**
 * GET {base}/latest?v=&os=&arch= on our own API, falling back to the legacy
 * CDN manifest so update checks keep working if the API is down.
 *
 * @param {{ force?: boolean, counted?: boolean, version?: string, os?: string, arch?: string }} [options]
 *   `counted` (default true) picks the endpoint: `/latest` records an
 *   update-check metric, `/manifest` returns the identical body and records
 *   nothing. An uncounted fetch never stamps the day — see writeCachedManifest.
 * @returns {Promise<object | null>}
 */
export async function fetchLatestManifest(options = {}) {
  const { force = false, version = '', os = '', arch = '' } = options;
  // A recorded demo or e2e run must not count as an active device. `/manifest`
  // returns the identical body and records nothing, so a driven run still gets
  // its update information and the ledger stays about real installs.
  const counted = options.counted !== false && !isSyntheticRun();

  if (!force) {
    const cached = readCachedManifest(counted ? version : '');
    if (cached) return cached;
  }

  const baseUrl = getUpdatesBaseUrl();
  const fallbackUrl = getUpdatesFallbackUrl();

  if (baseUrl) {
    try {
      const manifest = await fetchJson(
        counted
          ? buildLatestManifestUrl(baseUrl, { version, os, arch, repeat: isRepeatCheck() })
          : buildManifestUrl(baseUrl),
        CHECK_TIMEOUT_MS
      );
      writeCachedManifest(manifest, { stampDay: counted, version });
      if (counted) markLatestReached();
      return manifest;
    } catch (err) {
      console.info(
        '[updateService] Updates API unreachable, falling back to CDN:',
        err?.message || err
      );
    }
  } else {
    console.warn('[updateService] REACT_APP_UPDATES_BASE_URL is not set');
  }

  // The CDN origin is inherently uncounted, but a COUNTED request that fell
  // back to it still spends the device's call for the half-day: the intent, not the
  // host that answered, is what the ledger records.
  const manifest = await fetchJson(fallbackUrl);
  writeCachedManifest(manifest, { stampDay: counted, version });
  return manifest;
}

async function getPlatformContext() {
  try {
    const { platform, arch } = await import('@tauri-apps/plugin-os');
    return {
      platformKey: resolvePlatformKey(platform(), arch()),
      osPlatform: platform(),
      cpuArch: arch(),
    };
  } catch {
    return { platformKey: null, osPlatform: '', cpuArch: '' };
  }
}

/**
 * @returns {Promise<string>}
 */
export async function getAppVersion() {
  if (isTauri()) {
    try {
      return await getVersion();
    } catch {
      // fall through to package version
    }
  }
  return packageJson.version || '';
}

/**
 * @typedef {object} UpdateCheckResult
 * @property {boolean} updateAvailable
 * @property {string} currentVersion
 * @property {object | null} latest
 * @property {object | null} artifact
 * @property {string} [error]
 * @property {UpdateErrorCode} [errorCode]
 */

/**
 * @param {{ ignoreDismissed?: boolean, quiet?: boolean, force?: boolean, counted?: boolean }} [options]
 *   `force` bypasses the per-UTC-half-day manifest cache — set it for
 *   user-initiated "check for updates now". `counted` (default true) must be
 *   FALSE for any such user-initiated check, or every click registers as
 *   another active device.
 * @returns {Promise<UpdateCheckResult>}
 */
export async function checkForUpdates(options = {}) {
  const {
    ignoreDismissed = false,
    quiet = false,
    force = false,
    counted = true,
  } = options;

  if (!isTauri()) {
    return {
      updateAvailable: false,
      currentVersion: await getAppVersion(),
      latest: null,
      artifact: null,
    };
  }

  try {
    const currentVersion = await getVersion();
    const prefs = await loadAppPreferences();
    // The skip is what zeroes updateAvailable, which is what darkens the badge.
    // The DAILY CAP is deliberately not here — it lives only in
    // updatePromptPolicy.js, so that it can never reach the badge.
    const skippedVersion = ignoreDismissed ? null : prefs.skippedUpdateVersion || null;

    const { platformKey, osPlatform, cpuArch } = await getPlatformContext();

    const manifest = await fetchLatestManifest({
      force,
      counted,
      version: currentVersion,
      os: osPlatform,
      arch: cpuArch,
    });
    if (!manifest) {
      return {
        updateAvailable: false,
        currentVersion,
        latest: null,
        artifact: null,
      };
    }

    const artifact = platformKey ? manifest.platforms?.[platformKey] ?? null : null;

    if (!artifact) {
      if (platformKey) {
        console.info('[updateService] No artifact for platform', platformKey);
      }
      return {
        updateAvailable: false,
        currentVersion,
        latest: manifest,
        artifact: null,
        artifactVersion: '',
      };
    }

    // Judged against the version THIS PLATFORM's entry declares for itself, not
    // the manifest headline — they differ whenever an entry was carried forward
    // from an earlier build (see resolveArtifactVersion).
    const available = isUpdateAvailable(currentVersion, manifest, skippedVersion, artifact);
    const artifactVersion = resolveArtifactVersion(manifest, artifact);

    return {
      updateAvailable: available,
      currentVersion,
      latest: manifest,
      artifact: available ? artifact : null,
      artifactVersion,
    };
  } catch (err) {
    const { code, message } = classifyUpdateError(err);
    if (!quiet) {
      console.warn('[updateService] Update check failed:', message);
    } else if (code !== 'access_denied' && code !== 'not_found') {
      console.info('[updateService] Background update check failed:', message);
    }
    return {
      updateAvailable: false,
      currentVersion: '',
      latest: null,
      artifact: null,
      error: message,
      errorCode: code,
    };
  }
}

/**
 * Preferences the prompt policy needs, read from the same place
 * `checkForUpdates` reads its own — so the hook never depends on the
 * preferences store having hydrated first.
 * @returns {Promise<{ skippedUpdateVersion: string|null, updatePromptLastShownDay: string|null, checkUpdatesFrequency: string }>}
 */
export async function readUpdatePromptPrefs() {
  const prefs = await loadAppPreferences();
  return {
    skippedUpdateVersion: prefs.skippedUpdateVersion || null,
    updatePromptLastShownDay: prefs.updatePromptLastShownDay || null,
    checkUpdatesFrequency: prefs.checkUpdatesFrequency,
  };
}

/**
 * "Skip this version" — a permanent, per-version refusal. Records the ARTIFACT
 * version, never the manifest headline (the two differ whenever an entry was
 * carried forward), and mirrors the legacy key so a downgrade keeps the skip.
 * @param {string} version
 */
export async function skipUpdateVersion(version) {
  const prefs = await loadAppPreferences();
  await saveAppPreferences(
    { ...prefs, skippedUpdateVersion: version, dismissedUpdateVersion: version },
    { immediate: true }
  );
}

/**
 * The daily cap. Written when the modal AUTO-ANNOUNCES itself — never by a
 * dismissal handler, and never by a modal the user opened themselves.
 * @param {string} dayStamp "YYYY-MM-DD" UTC
 */
export async function recordUpdatePromptShown(dayStamp) {
  const prefs = await loadAppPreferences();
  await saveAppPreferences(
    { ...prefs, updatePromptLastShownDay: dayStamp },
    { immediate: true }
  );
}

/**
 * @param {object} artifact
 * @param {string} [expectedVersion] the version the manifest DECLARED for these
 *   bytes. Rust checks the downloaded artifact really is that version, and that
 *   it is newer than what is installed — the checksum alone cannot tell the two
 *   apart, because whoever writes the manifest also writes the checksum.
 * @returns {Promise<string>} local installer path
 */
export async function downloadUpdateArtifact(artifact, expectedVersion = '') {
  // Manifest artifact paths are relative to the CDN the binaries live on, not
  // to the updates API the manifest is now served from.
  const downloadUrl = resolveArtifactUrl(getUpdatesArtifactBaseUrl(), artifact.url);
  if (!downloadUrl) {
    throw new Error('Invalid update artifact URL');
  }

  const sha256 = artifact.sha256 && artifact.sha256.trim() ? artifact.sha256.trim() : null;
  if (!sha256) {
    throw new Error('Update artifact is missing a sha256 checksum — cannot verify integrity');
  }

  const result = await invoke('download_update', {
    request: {
      url: downloadUrl,
      filename: artifact.filename,
      sha256,
      expectedVersion: expectedVersion || null,
    },
  });

  return result.path;
}

/**
 * @param {object} artifact
 * @param {string} [expectedVersion] manifest-declared version, passed through to
 *   both Rust commands. This side is a convenience: `open_update_installer`
 *   re-derives the artifact's real version from disk and enforces the
 *   anti-rollback rule whether or not this argument arrives.
 */
export async function downloadAndInstall(artifact, expectedVersion = '') {
  const localPath = await downloadUpdateArtifact(artifact, expectedVersion);

  await invoke('open_update_installer', {
    path: localPath,
    expectedVersion: expectedVersion || null,
  });

  // NOT `getCurrentWindow().close()`, which is what this used to do.
  //
  // Close-to-tray is ON by default and `on_window_event` swallows EVERY
  // `CloseRequested` while it is on: the window hid, the process stayed alive,
  // and a live app is exactly what a macOS `.dmg` cannot replace and what the
  // Windows NSIS installer refuses to install over. "I clicked update, the app
  // disappeared, I'm still on the old version."
  //
  // `quit_for_update` terminates the process instead, without touching the
  // user's close-to-tray setting — ordinary closes still go to the tray. It
  // returns as soon as the exit is scheduled (Rust waits out a short grace
  // window first so the OS can finish launching the installer), so this resolve
  // is the signal to tear down the UI, not proof the app has gone.
  await invoke('quit_for_update');
}
